// Search store: manages a MiniSearch index built from the server's search-index
// endpoint. The index is loaded once per firm and fully refreshed (not patched)
// after any CRUD mutation that affects searchable data.

// library imports
import { defineStore } from 'pinia';
import { ref } from 'vue';
import MiniSearch from 'minisearch';

// local imports
import { trpc } from '@/trpc';
import { logger } from '@/utils/logger';
import {
  GROUP_ORDER,
  GROUP_CAPS,
  GROUP_LABELS,
  type EntityType,
  type SearchIndexEntry,
  type SearchResultItem,
  type SearchGroup,
} from '@/types/search';

////////////////////////////////
// MiniSearch factory

/**
 * Create a fresh MiniSearch instance configured for the search-index schema.
 *
 * @returns configured MiniSearch instance
 */
function createIndex(): MiniSearch<SearchIndexEntry> {
  return new MiniSearch<SearchIndexEntry>({
    idField: 'id',
    fields: ['primary', 'secondary'],
    storeFields: ['id', 'entityType', 'entityId', 'label', 'subLabel', 'routing'],
    searchOptions: {
      prefix: true,
      fuzzy: 0.2,
      boost: {
        primary: 3,
        secondary: 1,
      },
    },
  });
}

////////////////////////////////
// Grouping + capping

const FILTERED_CAP = 20;

/**
 * Group raw MiniSearch results by entity type and cap each group.
 *
 * @param results - raw MiniSearch search results
 * @param filter - optional entity type filter; when set, only that type is returned
 * @returns array of SearchGroup in display order
 */
function groupAndCap(
  results: Array<SearchResultItem>,
  filter?: EntityType | null,
): SearchGroup[] {
  const byType = new Map<EntityType, SearchResultItem[]>();
  for (const r of results) {
    if (filter && r.entityType !== filter)
      continue;

    const list = byType.get(r.entityType) ?? [];
    list.push(r);
    byType.set(r.entityType, list);
  }

  const groups: SearchGroup[] = [];
  for (const type of GROUP_ORDER) {
    const items = byType.get(type);
    if (!items || !items.length)
      continue;

    const cap = filter ? FILTERED_CAP : GROUP_CAPS[type];
    groups.push({
      type,
      label: GROUP_LABELS[type],
      results: items.slice(0, cap),
    });
  }

  return groups;
}

////////////////////////////////
// Store

export const useSearchStore = defineStore('search', () => {
  ////////////////////////////////
  // data
  const isReady = ref(false);
  const isLoading = ref(false);
  const bootstrapError = ref(false);
  const firmId = ref<string | null>(null);

  // Non-reactive for performance — MiniSearch is a large mutable object that
  // Vue's reactivity proxy would slow down significantly.
  let index: MiniSearch<SearchIndexEntry> | null = null;

  // The in-flight fresh load, shared by concurrent callers so one firm's index is never
  // fetched and rebuilt twice at once. Tracks the firm so a switch supersedes it.
  let inFlight: { firmId: string; promise: Promise<void> } | null = null;

  // Mutation-triggered refreshes arrive in bursts — one save can settle across several
  // stores, each asking to rebuild the same ~1.7MB payload. Collapse them into one.
  let refreshTimer: ReturnType<typeof setTimeout> | null = null;
  const REFRESH_DEBOUNCE_MS = 1000;

  ////////////////////////////////
  // internal

  /**
   * Fetch the search-index payload and (re)build the MiniSearch index.
   *
   * Behavior adapts based on whether this is a first load or a background refresh:
   * - First load / firm switch: sets isLoading, clears old index, shows loading
   *   state. On error, sets bootstrapError.
   * - Same firm, already ready: background refresh — current index keeps serving
   *   queries throughout. On error, silently continues with existing index.
   *
   * @param fId - the firm ID to load the index for
   * @param isFreshLoad - whether this is a first load / firm switch
   */
  async function fetchAndBuild(fId: string, isFreshLoad: boolean): Promise<void> {
    if (isFreshLoad) {
      isLoading.value = true;
      isReady.value = false;
      bootstrapError.value = false;
      firmId.value = fId;
      index = null;
    }

    try {
      /*
       * DELIBERATE RAW STORE READ. Every other store read moved to a scoped `useQuery`
       * in a composable; this one stays a raw `trpc` call inside the store on purpose.
       * Global search must stay live and unrewound — a date change must not rewind or
       * refetch the index — so it must not be partitioned by the query scope. Its only
       * dimension is the firm, supplied by `BaseWindow.vue`'s watcher on
       * `route.params.firmId`, which calls `loadIndex(firmId)`. Living outside the scope
       * is not enough on its own: a deep link on an ?asOf URL activates the dated scope
       * before this fetch fires, so `searchIndex.*` is in `ALWAYS_LIVE_PATHS`
       * (src/trpc/client.ts), which strips the X-As-Of-Date header from the request.
       */
      const payload = await trpc.searchIndex.getAll.query();
      const newIndex = createIndex();

      // addAllAsync, not addAll: indexing the firm's entities is a ~400ms synchronous
      // block, long enough to stall unrelated in-flight query continuations behind it.
      // Chunking yields to the event loop between batches.
      await newIndex.addAllAsync(payload.entities, { chunkSize: 500 });
      index = newIndex;

      if (isFreshLoad)
        isReady.value = true;
    } catch (err) {
      if (isFreshLoad) {
        bootstrapError.value = true;
      } else {
        logger.warn('[search] Background refresh failed — index may be stale', err);
      }
    } finally {
      if (isFreshLoad)
        isLoading.value = false;
    }
  }

  ////////////////////////////////
  // methods

  /**
   * Load or refresh the firm's search index.
   *
   * A first load or firm switch runs immediately, deduplicated so concurrent callers
   * share one fetch instead of each pulling the payload down again. A refresh for a firm
   * that is already loaded — i.e. after a mutation — is debounced: the index is only read
   * when the user opens search, so a burst of store settles should cost one rebuild
   * rather than one apiece.
   *
   * @param fId - the firm ID to load the index for
   * @returns a promise that settles when a fresh load completes. Debounced refreshes
   *   resolve immediately — their callers fire and forget.
   */
  async function loadIndex(fId: string): Promise<void> {
    const isFreshLoad = fId !== firmId.value || !isReady.value;

    if (isFreshLoad) {
      if (inFlight && inFlight.firmId === fId)
        return inFlight.promise;

      const promise = fetchAndBuild(fId, true).finally(() => {
        if (inFlight?.promise === promise)
          inFlight = null;
      });
      inFlight = { firmId: fId, promise };

      return promise;
    }

    if (refreshTimer)
      clearTimeout(refreshTimer);

    refreshTimer = setTimeout(() => {
      refreshTimer = null;
      void fetchAndBuild(fId, false);
    }, REFRESH_DEBOUNCE_MS);
  }

  /**
   * Search the index. Returns grouped and capped results in display order.
   *
   * @param query - the search query string
   * @param filter - optional entity type to restrict results to
   * @returns array of SearchGroup
   */
  function search(query: string, filter?: EntityType | null): SearchGroup[] {
    const q = query.trim();
    if (!q || !index)
      return [];

    const raw = index.search(q) as unknown as SearchResultItem[];
    return groupAndCap(raw, filter);
  }

  /**
   * Look up the sub-label for a specific entity from the stored index fields.
   *
   * @param entityType - the entity type (e.g. 'company', 'fund')
   * @param entityId - the entity's own ID
   * @returns the sub-label string, or undefined if the entry is not in the index
   */
  function getSubLabel(entityType: string, entityId: string): string | undefined {
    if (!index)
      return undefined;

    const stored = index.getStoredFields(`${entityType}:${entityId}`);
    return stored?.subLabel as string | undefined;
  }

  /**
   * Clear all state — called on logout or when search context is no longer needed.
   */
  function reset() {
    // Drop any scheduled or in-flight work first: on logout a pending refresh would
    // otherwise fire afterwards and rebuild the index for the firm just left.
    if (refreshTimer) {
      clearTimeout(refreshTimer);
      refreshTimer = null;
    }
    inFlight = null;

    isReady.value = false;
    isLoading.value = false;
    bootstrapError.value = false;
    firmId.value = null;
    index = null;
  }

  ////////////////////////////////
  // public interface
  return {
    isReady,
    isLoading,
    bootstrapError,
    firmId,

    loadIndex,
    search,
    getSubLabel,
    reset,
  };
});
